import JSZip from 'jszip';
import { supabase } from '../lib/supabase';
import { getCategories } from './avatarAssetService';
import { AVATAR_ASSET_BUCKET, buildAssetStoragePath, AVATAR_ASSET_ALLOWED_MIME } from '../config/storage';
import { parseAvatarFilename, mapParsedToDbPayload } from './avatarFilenameParser';
import { PARSER_STATUS } from '../config/avatarParserConfig';

// ─── SUPPORTED FILE TYPES ──────────────────────────────────────────────────────

const SUPPORTED_MIME = AVATAR_ASSET_ALLOWED_MIME;

// ─── BUCKET VALIDATION ────────────────────────────────────────────────────────

/**
 * Validates that the avatar-assets storage bucket exists and is accessible.
 * If the bucket does not exist, attempts to create it automatically.
 * Returns { exists: boolean, isPublic: boolean, error: string|null }
 */
export const validateAvatarBucket = async () => {
  try {
    const { data: buckets, error } = await supabase?.storage?.listBuckets();
    if (error) {
      // listBuckets may fail due to RLS — try to verify bucket directly
      const { error: listError } = await supabase?.storage?.from(AVATAR_ASSET_BUCKET)?.list('', { limit: 1 });
      if (!listError) {
        return { exists: true, isPublic: true, error: null };
      }
      return { exists: false, isPublic: false, error: error?.message };
    }
    const bucket = buckets?.find(b => b?.id === AVATAR_ASSET_BUCKET);
    if (!bucket) {
      // Bucket not visible in list (possible RLS on storage.buckets) — verify by direct access
      const { error: listError } = await supabase?.storage?.from(AVATAR_ASSET_BUCKET)?.list('', { limit: 1 });
      if (!listError) {
        // Bucket exists and is accessible even though listBuckets didn't show it
        return { exists: true, isPublic: true, error: null };
      }

      // Attempt to create the bucket automatically
      const { data: created, error: createError } = await supabase?.storage?.createBucket(AVATAR_ASSET_BUCKET, {
        public: true,
        fileSizeLimit: 10485760,
        allowedMimeTypes: ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'],
      });
      if (createError) {
        // If creation failed due to RLS or "already exists", try direct access one more time
        const { error: retryListError } = await supabase?.storage?.from(AVATAR_ASSET_BUCKET)?.list('', { limit: 1 });
        if (!retryListError) {
          // Bucket exists and is accessible — creation error was spurious (e.g. already exists)
          return { exists: true, isPublic: true, error: null };
        }
        return {
          exists: false,
          isPublic: false,
          error: `Avatar asset bucket '${AVATAR_ASSET_BUCKET}' is not configured and could not be created: ${createError?.message}`,
        };
      }
      return { exists: true, isPublic: true, error: null, bucket: created, created: true };
    }
    return { exists: true, isPublic: bucket?.public === true, error: null, bucket };
  } catch (err) {
    return { exists: false, isPublic: false, error: err?.message || 'Unknown error checking bucket.' };
  }
};

// ─── INGEST JOB MANAGEMENT ────────────────────────────────────────────────────

export const createIngestJob = async (filename, userId) => {
  const { data, error } = await supabase?.from('avatar_asset_ingest_jobs')?.insert({
    filename,
    status: 'pending',
    created_by: userId,
    started_at: new Date()?.toISOString(),
  })?.select()?.single();
  if (error) throw error;
  return data;
};

export const updateIngestJob = async (jobId, updates) => {
  const { data, error } = await supabase?.from('avatar_asset_ingest_jobs')?.update(updates)?.eq('id', jobId)?.select()?.single();
  if (error) throw error;
  return data;
};

export const getIngestJobs = async () => {
  const { data, error } = await supabase?.from('avatar_asset_ingest_jobs')?.select('*, user_profiles (full_name, email)')?.order('created_at', { ascending: false });
  if (error) throw error;
  return data || [];
};

export const getIngestJobItems = async (jobId) => {
  const { data, error } = await supabase?.from('avatar_asset_ingest_items')?.select('*')?.eq('job_id', jobId)?.order('created_at', { ascending: true });
  if (error) throw error;
  return data || [];
};

// ─── ZIP PREVIEW (client-side, no upload) ─────────────────────────────────────

/**
 * Preview ZIP contents using the robust parser.
 * Returns array of parsed metadata per file — no uploads performed.
 */
export const previewZip = async (zipFile) => {
  const zip = new JSZip();
  const loaded = await zip?.loadAsync(zipFile);
  const previews = [];

  for (const [relativePath, zipEntry] of Object.entries(loaded?.files)) {
    if (zipEntry?.dir) continue;

    let parsed = parseAvatarFilename(relativePath);

    previews?.push({
      relativePath,
      filename: parsed?.original_filename,
      supported: parsed?.parser_status !== PARSER_STATUS?.INVALID,
      category: parsed?.category,
      slug: parsed?.slug,
      display_label: parsed?.display_label,
      layer_order: parsed?.layer_order,
      // Metadata fields
      body_type_affinity: parsed?.body_type_affinity,
      gender_affinity: parsed?.gender_affinity,
      color_family: parsed?.color_family,
      eye_color: parsed?.eye_color,
      hair_color: parsed?.hair_color,
      skin_tone: parsed?.skin_tone,
      style_variant: parsed?.style_variant,
      special_type: parsed?.special_type,
      subcategory: parsed?.subcategory,
      variant: parsed?.variant,
      // Parser state
      parser_status: parsed?.parser_status,
      parser_warnings: parsed?.parser_warnings,
      parser_version: parsed?.parser_version,
      mime_type: parsed?.mime_type,
      // Legacy compat
      isAmbiguous: parsed?.parser_status === PARSER_STATUS?.REVIEW_REQUIRED,
      ambiguityReason: parsed?.parser_warnings?.join('; ') || null,
      // Full parsed object for editing
      _parsed: parsed,
    });
  }

  return previews;
};

// ─── FULL INGEST ───────────────────────────────────────────────────────────────

/**
 * Ingest a ZIP file into Supabase storage and avatar_assets table.
 * Uses the robust parser for all filename normalization.
 * Accepts optional overrides map (keyed by relativePath) for editor-corrected metadata.
 *
 * @param {File} zipFile
 * @param {string} userId
 * @param {function} onProgress
 * @param {object} overrides - { [relativePath]: { category, style_variant, color_family, subcategory, ... } }
 * @param {Set<string>} skippedPaths - set of relativePaths to skip
 */
export const ingestZip = async (zipFile, userId, onProgress, overrides = {}, skippedPaths = new Set()) => {
  // ── BUCKET VALIDATION: abort early if bucket is missing ──────────────────
  const bucketCheck = await validateAvatarBucket();
  if (!bucketCheck?.exists) {
    throw new Error(bucketCheck?.error || `Avatar asset bucket '${AVATAR_ASSET_BUCKET}' is not configured.`);
  }

  const categories = await getCategories();
  const categoryMap = Object.fromEntries(categories?.map(c => [c?.slug, c?.id]));

  const job = await createIngestJob(zipFile?.name, userId);
  const jobId = job?.id;

  await updateIngestJob(jobId, { status: 'processing' });

  const zip = new JSZip();
  const loaded = await zip?.loadAsync(zipFile);

  const fileEntries = Object.entries(loaded?.files)?.filter(([, e]) => !e?.dir);
  const total = fileEntries?.length;
  let succeeded = 0, failed = 0, skipped = 0, needsReview = 0;

  await updateIngestJob(jobId, { total_files: total });

  const itemInserts = [];

  for (let i = 0; i < fileEntries?.length; i++) {
    const [relativePath, zipEntry] = fileEntries?.[i];

    if (onProgress) onProgress({ current: i + 1, total, filename: relativePath?.split('/')?.pop() });

    // Skip explicitly unchecked files
    if (skippedPaths?.has(relativePath)) {
      skipped++;
      itemInserts?.push({
        job_id: jobId,
        original_filename: relativePath,
        status: 'skipped',
        error_message: 'Skipped by editor before ingest.',
      });
      continue;
    }

    // Parse using robust parser
    let parsed = parseAvatarFilename(relativePath);

    // Apply editor overrides if present
    if (overrides?.[relativePath]) {
      const ov = overrides?.[relativePath];
      parsed = {
        ...parsed,
        category: ov?.category ?? parsed?.category,
        style_variant: ov?.style_variant ?? parsed?.style_variant,
        color_family: ov?.color_family ?? parsed?.color_family,
        subcategory: ov?.subcategory ?? parsed?.subcategory,
        variant: ov?.variant ?? parsed?.variant,
        gender_affinity: ov?.gender_affinity ?? parsed?.gender_affinity,
        body_type_affinity: ov?.body_type_affinity ?? parsed?.body_type_affinity,
        // Recalculate storage path with potentially corrected category
        storage_path: ov?.category
          ? `v1/${ov?.category}/${parsed?.original_filename}`
          : parsed?.storage_path,
        // Mark as manually reviewed
        parser_status: PARSER_STATUS?.VALID,
        parser_warnings: [...(parsed?.parser_warnings || []), 'Metadata manually corrected by editor.'],
      };
    }

    // Skip invalid (unsupported extension, empty filename)
    if (parsed?.parser_status === PARSER_STATUS?.INVALID) {
      skipped++;
      itemInserts?.push({
        job_id: jobId,
        original_filename: relativePath,
        parsed_category: parsed?.category,
        parsed_metadata: { parser_warnings: parsed?.parser_warnings },
        status: 'skipped',
        error_message: parsed?.parser_warnings?.join('; ') || 'Invalid file.',
      });
      continue;
    }

    // Flag review_required items without overrides
    if (parsed?.parser_status === PARSER_STATUS?.REVIEW_REQUIRED && !overrides?.[relativePath]) {
      needsReview++;
      itemInserts?.push({
        job_id: jobId,
        original_filename: relativePath,
        parsed_category: parsed?.category,
        parsed_metadata: {
          display_label: parsed?.display_label,
          parser_warnings: parsed?.parser_warnings,
          parser_status: parsed?.parser_status,
        },
        status: 'ambiguous',
        error_message: parsed?.parser_warnings?.join('; ') || 'Needs review.',
      });
      continue;
    }

    try {
      // Get file blob
      const blob = await zipEntry?.async('blob');
      const fileBlob = new File([blob], parsed?.original_filename, { type: parsed?.mime_type });

      // Upload to storage
      const storagePath = parsed?.storage_path || buildAssetStoragePath(parsed?.category, parsed?.original_filename);
      const { error: uploadError } = await supabase?.storage
        ?.from(AVATAR_ASSET_BUCKET)
        ?.upload(storagePath, fileBlob, { contentType: parsed?.mime_type, upsert: true });

      if (uploadError) throw uploadError;

      // Build DB payload using mapper
      const categoryId = categoryMap?.[parsed?.category] || null;
      const assetRecord = mapParsedToDbPayload(parsed, categoryId, userId);

      const { data: asset, error: assetError } = await supabase
        ?.from('avatar_assets')
        ?.upsert(assetRecord, { onConflict: 'slug' })
        ?.select()
        ?.single();

      if (assetError) throw assetError;

      succeeded++;
      itemInserts?.push({
        job_id: jobId,
        original_filename: relativePath,
        storage_path: storagePath,
        parsed_category: parsed?.category,
        parsed_metadata: {
          display_label: parsed?.display_label,
          slug: parsed?.slug,
          parser_status: parsed?.parser_status,
          parser_warnings: parsed?.parser_warnings,
        },
        status: 'success',
        asset_id: asset?.id,
      });
    } catch (err) {
      failed++;
      itemInserts?.push({
        job_id: jobId,
        original_filename: relativePath,
        status: 'failed',
        error_message: err?.message || 'Unknown error',
      });
    }
  }

  // Batch insert all item records
  if (itemInserts?.length > 0) {
    await supabase?.from('avatar_asset_ingest_items')?.insert(itemInserts);
  }

  // Finalize job
  const finalJob = await updateIngestJob(jobId, {
    status: failed > 0 && succeeded === 0 ? 'failed' : 'completed',
    succeeded,
    failed,
    skipped,
    ambiguous: needsReview,
    completed_at: new Date()?.toISOString(),
  });

  return { job: finalJob, succeeded, failed, skipped, needsReview, total };
};

// ─── RETRY FAILED ITEMS ────────────────────────────────────────────────────────

export const retryFailedItems = async (jobId, userId) => {
  const items = await getIngestJobItems(jobId);
  const failedItems = items?.filter(i => i?.status === 'failed');
  if (failedItems?.length > 0) {
    await supabase?.from('avatar_asset_ingest_items')?.update({ status: 'skipped', error_message: 'Marked for retry by ' + userId })?.in('id', failedItems?.map(i => i?.id));
  }
  return failedItems?.length;
};

// ─── CSV EXPORT ────────────────────────────────────────────────────────────────

export const exportFailedItemsCSV = (items) => {
  const headers = ['original_filename', 'parsed_category', 'status', 'error_message'];
  const rows = items?.filter(i => i?.status === 'failed' || i?.status === 'ambiguous')?.map(i => [
    `"${i?.original_filename}"`,
    `"${i?.parsed_category || ''}"`,
    `"${i?.status}"`,
    `"${(i?.error_message || '')?.replace(/"/g, '""')}"`,
  ]?.join(','));
  return [headers?.join(','), ...rows]?.join('\n');
};
